import { FunctionComponent, RefObject, useEffect, useState } from "react";

import { Tooltip, TooltipProps } from "primereact/tooltip";

interface ITooltipWrapperProps extends Omit<TooltipProps, "ref" | "target"> {
  targetRef?: RefObject<HTMLElement | null> | string;
  tooltipRef?: RefObject<Tooltip | null>;
  template?: React.ReactNode;
}

export const TooltipWrapper: FunctionComponent<ITooltipWrapperProps> = (props: ITooltipWrapperProps) => {
  if (!props?.targetRef) {
    return;
  }

  const resolveTargetRef = (targetRef?: RefObject<HTMLElement | null> | string): HTMLElement | string | null => {
    if (!targetRef) {
      return null;
    }
    if (typeof targetRef === "string") {
      return targetRef;
    }
    return (targetRef as RefObject<HTMLElement | null>)?.current ?? null;
  };

  const [target, setTarget] = useState<HTMLElement | string | null>(resolveTargetRef(props.targetRef));

  // #54082 #57423: Set the target inside the effect because the target element (selector or ref.current)
  // may not exist on the initial render (because the tooltip may be mounted before the DOM ref is populated,
  // so it has no target and will not show until a later re-render).
  useEffect(() => {
    if (typeof props.targetRef === "string") {
      setTarget(props.targetRef);
      return;
    }

    const resolved = resolveTargetRef(props.targetRef);
    if (resolved) {
      setTarget(resolved);
      return;
    }

    let cancelled = false;
    const interval = setInterval(() => {
      const el = resolveTargetRef(props.targetRef);
      if (el) {
        if (!cancelled) {
          setTarget(el);
        }
        clearInterval(interval);
      }
    }, 100);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [props.targetRef]);

  if (!target) {
    // Do not render Tooltip until a valid target is available
    return null;
  }

  const tooltipProps = { ...props };
  if (tooltipProps.targetRef) {
    delete tooltipProps.targetRef;
  }
  if (tooltipProps.tooltipRef) {
    delete tooltipProps.tooltipRef;
  }

  return tooltipProps?.template ? (
    <Tooltip {...tooltipProps} ref={props.tooltipRef} target={target}>
      {tooltipProps?.template}
    </Tooltip>
  ) : (
    <Tooltip {...tooltipProps} ref={props.tooltipRef} target={target} />
  );
};
